/**
 * Google Maps custom styling configuration for shipment map
 */
export const mapStyles: google.maps.MapTypeStyle[] = [
  {
    elementType: 'geometry',

    stylers: [
      {
        color: '#ecf0f7',
      },
    ],
  },

  {
    featureType: 'administrative.country',

    elementType: 'geometry.stroke',

    stylers: [
      {
        color: '#818287',
      },
    ],
  },

  {
    featureType: 'administrative.country',

    elementType: 'labels.text.fill',

    stylers: [
      {
        color: '#000000',
      },
    ],
  },

  {
    featureType: 'administrative.locality',

    elementType: 'labels.text.fill',

    stylers: [
      {
        color: '#576173',
      },
    ],
  },

  {
    featureType: 'administrative.locality',

    elementType: 'labels.text.stroke',

    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },

  {
    featureType: 'administrative.neighborhood',

    elementType: 'labels.text.fill',

    stylers: [
      {
        color: '#85a0d5',
      },
    ],
  },

  {
    featureType: 'administrative.neighborhood',

    elementType: 'labels.text.stroke',

    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },

  {
    featureType: 'administrative.province',

    elementType: 'geometry.stroke',

    stylers: [
      {
        color: '#f5b2a6',
      },
    ],
  },

  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#a7e0b6',
      },
    ],
  },

  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#DEE2E8',
      },
    ],
  },

  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },

  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#859ad0',
      },
    ],
  },

  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },

  {
    featureType: 'transit.station.airport',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#cad4e0',
      },
    ],
  },

  {
    featureType: 'water',
    stylers: [
      {
        color: '#c6dbff',
      },
    ],
  },
];

/**
 * Dark-mode counterpart of `mapStyles`, mirroring the same feature/element
 * targeting with colors adapted for a dark canvas (Google's Maps JS API
 * doesn't support a mapId + inline `styles` at the same time, and the app's
 * maps aren't bound to a mapId, so this is applied directly rather than via
 * a second Cloud Console style).
 */
export const darkMapStyles: google.maps.MapTypeStyle[] = [
  {
    // Deliberately NOT the same value as --surface-container (#17191e) --
    // land is the base 'geometry' fill for the whole map, so if it matches
    // the app's own dark panel background the map reads as an extension of
    // the UI chrome rather than a distinct surface with its own boundary.
    // Slightly more blue-tinted than before for a "console" feel.
    elementType: 'geometry',
    stylers: [{ color: '#161b24' }],
  },
  // Blanket default for every label (roads, POIs, transit, water, parks,
  // etc.) that the light style leaves un-styled -- without this they keep
  // the API's baked-in light-mode fill + white halo stroke, which reads as
  // a washed-out white smear on a dark map. The more specific administrative
  // overrides below refine particular label types on top of this base.
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9aa5b8' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#101317' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4a4d54' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#101317' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#e5e7eb' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9aa5b8' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#101317' }],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7ba3e8' }],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#101317' }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#8a5850' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [{ color: '#2f5c3c' }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.fill',
    stylers: [{ color: '#363b46' }],
  },
  {
    // Subtle cyan-gray rather than neutral gray, so the road network reads
    // as part of the same HUD/console palette as the highways and routes.
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#31404f' }],
  },
  {
    // Pushed toward the app's own bright accent blue (--info dark value)
    // rather than the previous muted blue-gray, to match route polylines
    // and other HUD accents.
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [{ color: '#5B9DFF' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit.station.airport',
    elementType: 'geometry.fill',
    stylers: [{ color: '#39485c' }],
  },
  {
    // More saturated/vivid than before -- the previous value read as flat
    // and dull next to the rest of the HUD palette.
    featureType: 'water',
    stylers: [{ color: '#0f4c92' }],
  },
];

/** Picks the light or dark style array for the app's current appearance. */
export function getMapStyles(
  darkModeEnabled: boolean
): google.maps.MapTypeStyle[] {
  return darkModeEnabled ? darkMapStyles : mapStyles;
}

export const mapContainerStyle = {
  width: '100%',
  height: '100%',
};
